import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { SystemEvent } from '@/hooks/useSystemEvents';

/* ── Producer search ── */

export interface HostDebugProducer {
  id: string;
  name: string;
  slug: string | null;
  status: string;
  place_id: string | null;
  created_at: string;
  email: string | null;
}

export function useHostDebugSearch(query: string) {
  return useQuery({
    queryKey: ['host-debug-search', query],
    enabled: query.length >= 2,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<HostDebugProducer[]> => {
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(query);

      if (isUuid) {
        const { data, error } = await supabase
          .from('producers')
          .select('id, name, slug, status, place_id, created_at, email')
          .eq('id', query)
          .limit(1);
        if (error) throw error;
        return (data ?? []) as HostDebugProducer[];
      }

      const { data, error } = await supabase
        .from('producers')
        .select('id, name, slug, status, place_id, created_at, email')
        .or(`slug.ilike.%${query}%,email.ilike.%${query}%,name.ilike.%${query}%`)
        .limit(10);
      if (error) throw error;
      return (data ?? []) as HostDebugProducer[];
    },
  });
}

/* ── System events for a producer ── */

export function useHostSystemEvents(producerId: string | null) {
  return useQuery({
    queryKey: ['host-debug-system-events', producerId],
    enabled: !!producerId,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<SystemEvent[]> => {
      const { data, error } = await supabase
        .from('system_events')
        .select('*')
        .eq('producer_id', producerId!)
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as unknown as SystemEvent[];
    },
  });
}

/* ── Error summary (last 7 days) ── */

export interface DomainErrorCount {
  event_key: string;
  count: number;
}

export function useHostErrorSummary(producerId: string | null) {
  return useQuery({
    queryKey: ['host-debug-error-summary', producerId],
    enabled: !!producerId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<SystemEvent[]> => {
      const since = new Date();
      since.setDate(since.getDate() - 7);

      const { data, error } = await supabase
        .from('system_events')
        .select('id, event_key, level, created_at, message, producer_id, entity_id, request_id, domain, entity_type, source, context, actor_user_id, user_agent, ip')
        .eq('producer_id', producerId!)
        .in('level', ['warn', 'error'])
        .gte('created_at', since.toISOString())
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as unknown as SystemEvent[];
    },
  });
}

/* ── Activities ── */

export interface HostActivity {
  id: string;
  title: string;
  status: string;
  start_date: string;
  max_spots: number;
  updated_at: string;
  booking_count: number;
}

export function useHostActivities(producerId: string | null) {
  return useQuery({
    queryKey: ['host-debug-activities', producerId],
    enabled: !!producerId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<HostActivity[]> => {
      const { data, error } = await supabase
        .rpc('get_producer_events_with_bookings' as any, {
          p_producer_id: producerId!,
          p_limit: 20,
        });
      if (error) throw error;
      return (data ?? []).map((e: any) => ({
        id: e.id,
        title: e.title,
        status: e.status,
        start_date: e.start_date,
        max_spots: e.max_spots,
        updated_at: e.updated_at,
        booking_count: e.booked_spots ?? 0,
      }));
    },
  });
}

/* ── Bookings ── */

export interface HostBooking {
  id: string;
  guest_email: string;
  event_id: string;
  status: string;
  spots: number;
  created_at: string;
}

export function useHostBookings(producerId: string | null) {
  return useQuery({
    queryKey: ['host-debug-bookings', producerId],
    enabled: !!producerId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<HostBooking[]> => {
      const { data, error } = await supabase
        .from('event_bookings')
        .select('id, guest_email, event_id, status, spots, created_at')
        .eq('producer_id', producerId!)
        .order('created_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      return (data ?? []) as HostBooking[];
    },
  });
}

/* ── Media errors ── */

export function useHostMediaErrors(producerId: string | null) {
  return useQuery({
    queryKey: ['host-debug-media-errors', producerId],
    enabled: !!producerId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<SystemEvent[]> => {
      const { data, error } = await supabase
        .from('system_events')
        .select('*')
        .eq('producer_id', producerId!)
        .ilike('event_key', 'media_%')
        .order('created_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      return (data ?? []) as unknown as SystemEvent[];
    },
  });
}

/* ── Request ID lookup ── */

export function useRequestIdLookup(requestId: string | null) {
  return useQuery({
    queryKey: ['host-debug-request-id', requestId],
    enabled: !!requestId && requestId.length >= 8,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<SystemEvent[]> => {
      const { data, error } = await supabase
        .from('system_events')
        .select('*')
        .eq('request_id', requestId!)
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      return (data ?? []) as unknown as SystemEvent[];
    },
  });
}

/* ── Unified timeline ── */

export interface TimelineItem {
  id: string;
  timestamp: string;
  source: 'system_event' | 'activity' | 'booking' | 'media';
  level: 'error' | 'warn' | 'success' | 'info';
  title: string;
  detail: string | null;
  entity_id: string | null;
}

export function useHostTimeline(producerId: string | null) {
  const sysEvents = useHostSystemEvents(producerId);
  const activities = useHostActivities(producerId);
  const bookings = useHostBookings(producerId);

  // Also fetch recent media_assets for this producer
  const mediaAssets = useQuery({
    queryKey: ['host-debug-timeline-media', producerId],
    enabled: !!producerId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('media_assets')
        .select('id, created_at, kind, owner_type, mime')
        // Phase 1 stability: add owner_type so the query uses idx_media_assets_owner
        // instead of scanning by owner_id alone (which misses the composite index).
        .eq('owner_type', 'producer')
        .eq('owner_id', producerId!)
        .order('created_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      return data ?? [];
    },
  });

  const isLoading = sysEvents.isLoading || activities.isLoading || bookings.isLoading || mediaAssets.isLoading;

  const items: TimelineItem[] = [];

  // System events
  for (const ev of sysEvents.data ?? []) {
    items.push({
      id: `se-${ev.id}`,
      timestamp: ev.created_at,
      source: 'system_event',
      level: ev.level === 'error' ? 'error' : ev.level === 'warn' ? 'warn' : 'info',
      title: ev.event_key,
      detail: ev.message,
      entity_id: ev.entity_id,
    });
  }

  // Activities (created/updated)
  for (const a of activities.data ?? []) {
    items.push({
      id: `act-${a.id}`,
      timestamp: a.updated_at,
      source: 'activity',
      level: a.status === 'public' ? 'success' : 'info',
      title: `Aktivitet: ${a.title}`,
      detail: `Status: ${a.status} · ${a.booking_count} bokningar`,
      entity_id: a.id,
    });
  }

  // Bookings
  for (const b of bookings.data ?? []) {
    items.push({
      id: `bk-${b.id}`,
      timestamp: b.created_at,
      source: 'booking',
      level: b.status === 'confirmed' ? 'success' : b.status === 'cancelled' ? 'warn' : 'info',
      title: `Bokning: ${b.guest_email}`,
      detail: `${b.spots} platser · ${b.status}`,
      entity_id: b.event_id,
    });
  }

  // Media assets
  for (const m of mediaAssets.data ?? []) {
    items.push({
      id: `med-${m.id}`,
      timestamp: m.created_at,
      source: 'media',
      level: 'info',
      title: `Media: ${m.kind}`,
      detail: m.mime ?? null,
      entity_id: m.id,
    });
  }

  // Sort newest first, limit 30
  items.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  const timeline = items.slice(0, 30);

  return { data: timeline, isLoading };
}
